import { useState, useEffect, useRef, useCallback } from 'react';
import { withFileAuth } from '@/utils/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ArrowLeft, 
  Upload, 
  Save,
  Loader2,
  MapPin,
  Plus,
  Trash2,
  Move,
  Image,
  Grid3X3,
  Eye,
  Settings,
  Umbrella,
  Coffee,
  Users
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { TableMap } from '@/components/tables/TableMap';
import { TableActionsDialog } from '@/components/tables/TableActionsDialog';
import { useTableStates } from '@/components/tables/useTableStates';
import { TABLE_STATE_COLORS } from '@/theme/statusColors';

// Use relative path for API calls (works on any domain)
const API = typeof window !== 'undefined' && window.location.hostname !== 'localhost' 
  ? '/api' 
  : `${process.env.REACT_APP_BACKEND_URL}/api`;

const UNIT_ICONS = {
  table: { icon: Coffee, label: 'Mesa' },
  umbrella: { icon: Umbrella, label: 'Sombrilla' },
  booth: { icon: Users, label: 'Cabina' },
};

export default function VisualMap() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  
  const [spaces, setSpaces] = useState([]);
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [units, setUnits] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [mapImage, setMapImage] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  const [editMode, setEditMode] = useState(false);
  const [draggingUnit, setDraggingUnit] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  
  const [showUnitDialog, setShowUnitDialog] = useState(false);
  const [unitForm, setUnitForm] = useState({ name: '', type: 'table', capacity: 4 });
  const [actionUnit, setActionUnit] = useState(null);
  const { units: tableUnits, refresh: refreshTables, now: tablesNow } = useTableStates();

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedSpace) {
      fetchSpaceData(selectedSpace.space_id);
    }
  }, [selectedSpace]);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/spaces`);
      setSpaces(response.data);
      if (response.data.length > 0) {
        setSelectedSpace(response.data[0]);
      }
    } catch (error) {
      toast.error('Error al cargar espacios');
    } finally {
      setLoading(false);
    }
  };

  const fetchSpaceData = async (spaceId) => {
    try {
      const [unitsRes, sessionsRes] = await Promise.all([
        axios.get(`${API}/spaces/${spaceId}/units`),
        axios.get(`${API}/sessions/active`)
      ]);
      setUnits(unitsRes.data);
      setSessions(sessionsRes.data);
      
      // Load map image if exists
      setMapImage(selectedSpace?.map_image_url || null);
    } catch (error) {
      console.error('Error fetching space data:', error);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Solo se permiten archivos de imagen');
      return;
    }

    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('purpose', 'floor_plan');

    try {
      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      setMapImage(imageUrl);
      
      // Save to space
      if (selectedSpace) {
        await axios.put(`${API}/spaces/${selectedSpace.space_id}`, {
          map_image_url: imageUrl
        });
        toast.success('Imagen del mapa guardada');
      }
    } catch (error) {
      toast.error('Error al subir la imagen');
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleCanvasClick = (e) => {
    if (!editMode || !canvasRef.current) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    if (selectedUnit) {
      // Move selected unit
      updateUnitPosition(selectedUnit.unit_id, x, y);
      setSelectedUnit(null);
    }
  };

  const handleUnitClick = (e, unit) => {
    e.stopPropagation();
    if (editMode) {
      setSelectedUnit(unit);
    } else {
      // In view mode, show unit details or navigate
      toast.info(`${unit.name} - ${getUnitStatus(unit)}`);
    }
  };

  const handleUnitDragStart = (e, unit) => {
    if (!editMode) return;
    setDraggingUnit(unit);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleUnitDrop = (e) => {
    e.preventDefault();
    if (!draggingUnit || !canvasRef.current) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    updateUnitPosition(draggingUnit.unit_id, x, y);
    setDraggingUnit(null);
  };

  const updateUnitPosition = async (unitId, x, y) => {
    try {
      await axios.put(`${API}/units/${unitId}`, {
        position_x: Math.max(0, Math.min(100, x)),
        position_y: Math.max(0, Math.min(100, y))
      });
      
      setUnits(units.map(u => 
        u.unit_id === unitId 
          ? { ...u, position_x: x, position_y: y }
          : u
      ));
    } catch (error) {
      toast.error('Error al mover la unidad');
    }
  };

  const getUnitStatus = (unit) => {
    const session = sessions.find(s => s.unit_id === unit.unit_id);
    if (session) {
      return session.status === 'active' ? 'Ocupada' : 'Reservada';
    }
    return 'Libre';
  };

  const getUnitColor = (unit) => {
    const key = { Ocupada: 'occupied', Reservada: 'reserved' }[getUnitStatus(unit)] || 'free';
    const c = TABLE_STATE_COLORS[key];
    return { background: c.bg, border: `2px solid ${c.border || c.bg}` };
  };

  const createUnit = async () => {
    if (!unitForm.name || !selectedSpace) {
      toast.error('Ingresa un nombre');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/units`, {
        space_id: selectedSpace.space_id,
        name: unitForm.name,
        type: unitForm.type,
        capacity: unitForm.capacity,
        position_x: 50,
        position_y: 50
      });
      
      setUnits([...units, response.data]);
      refreshTables();
      setShowUnitDialog(false);
      setUnitForm({ name: '', type: 'table', capacity: 4 });
      toast.success('Unidad creada');
    } catch (error) {
      toast.error('Error al crear unidad');
    } finally {
      setSaving(false);
    }
  };

  const deleteUnit = async (unitId) => {
    if (!window.confirm('¿Eliminar esta unidad?')) return;
    
    try {
      await axios.delete(`${API}/units/${unitId}`);
      setUnits(units.filter(u => u.unit_id !== unitId));
      refreshTables();
      toast.success('Unidad eliminada');
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-heading text-xl font-semibold">Mapa Visual</h1>
              <p className="text-sm text-muted-foreground">Distribuye las mesas y sombrillas</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Space Selector */}
            <Select 
              value={selectedSpace?.space_id} 
              onValueChange={(v) => setSelectedSpace(spaces.find(s => s.space_id === v))}
            >
              <SelectTrigger className="w-48" data-testid="visual-map-space-select">
                <SelectValue placeholder="Seleccionar espacio" />
              </SelectTrigger>
              <SelectContent className="bg-card">
                {spaces.map((space) => (
                  <SelectItem key={space.space_id} value={space.space_id}>
                    {space.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Mode Toggle */}
            <div className="flex items-center gap-2 bg-muted rounded-lg p-1">
              <Button 
                variant={!editMode ? "default" : "ghost"} 
                size="sm"
                onClick={() => setEditMode(false)} data-testid="visual-map-view-mode"
              >
                <Eye className="w-4 h-4 mr-1" />
                Ver
              </Button>
              <Button 
                variant={editMode ? "default" : "ghost"} 
                size="sm"
                onClick={() => setEditMode(true)} data-testid="visual-map-edit-mode"
              >
                <Settings className="w-4 h-4 mr-1" />
                Editar
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex h-[calc(100vh-73px)]">
        {/* Sidebar - Edit Mode */}
        {editMode && (
          <aside className="w-64 bg-card border-r border-border p-4 space-y-4">
            <div>
              <h3 className="font-semibold mb-2">Imagen de Fondo</h3>
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                className="hidden"
                onChange={handleImageUpload}
              />
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingImage}
              >
                {uploadingImage ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                {mapImage ? 'Cambiar imagen' : 'Subir plano'}
              </Button>
              {mapImage && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="w-full mt-2 text-red-500"
                  onClick={() => setMapImage(null)}
                >
                  Quitar imagen
                </Button>
              )}
            </div>

            <div>
              <h3 className="font-semibold mb-2">Unidades</h3>
              <Button 
                className="w-full mb-3"
                onClick={() => setShowUnitDialog(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Nueva Unidad
              </Button>
              
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {units.map((unit) => (
                  <div 
                    key={unit.unit_id}
                    className={`flex items-center justify-between p-2 rounded-lg border cursor-pointer transition-colors ${
                      selectedUnit?.unit_id === unit.unit_id ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted'
                    }`}
                    onClick={() => setSelectedUnit(unit)}
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={getUnitColor(unit)} />
                      <span className="text-sm">{unit.name}</span>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-6 w-6"
                      onClick={(e) => { e.stopPropagation(); deleteUnit(unit.unit_id); }}
                    >
                      <Trash2 className="w-3 h-3 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {selectedUnit && (
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm font-medium mb-1">Unidad seleccionada:</p>
                <p className="text-lg font-bold">{selectedUnit.name}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Haz clic en el mapa para moverla
                </p>
              </div>
            )}
          </aside>
        )}

        {/* Main Canvas: mismo componente de mesas que el POS */}
        <main className="flex-1 p-6 overflow-auto">
          <TableMap
            key={`${selectedSpace?.space_id}-${editMode}`}
            units={tableUnits.filter(u => u.space_id === selectedSpace?.space_id)}
            mapImage={mapImage}
            showAmount
            now={tablesNow}
            planOnly
            editable={editMode}
            onPositionChange={async (id, x, y) => { await updateUnitPosition(id, x, y); refreshTables(); }}
            onSelect={(u) => (editMode ? setSelectedUnit(u) : setActionUnit(u))}
          />
        </main>
      </div>

      {/* Create Unit Dialog */}
      <Dialog open={showUnitDialog} onOpenChange={setShowUnitDialog}>
        <DialogContent className="sm:max-w-md bg-card">
          <DialogHeader>
            <DialogTitle>Nueva Unidad</DialogTitle>
            <DialogDescription>
              Agrega una mesa, sombrilla o cabina al mapa
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nombre</Label>
              <Input
                placeholder="Ej: Mesa 5, Sombrilla 12"
                value={unitForm.name}
                onChange={(e) => setUnitForm({ ...unitForm, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select 
                value={unitForm.type} 
                onValueChange={(v) => setUnitForm({ ...unitForm, type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card">
                  {Object.entries(UNIT_ICONS).map(([key, { label }]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Capacidad</Label>
              <Input
                type="number"
                min="1"
                value={unitForm.capacity}
                onChange={(e) => setUnitForm({ ...unitForm, capacity: parseInt(e.target.value) || 1 })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUnitDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={createUnit} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Crear
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TableActionsDialog unit={actionUnit} units={tableUnits} open={!!actionUnit}
        onOpenChange={(v) => !v && setActionUnit(null)} onDone={refreshTables}
        onOpenDetails={(u) => navigate(`/pos?unit=${u.unit_id}`)} />
    </div>
  );
}
